/**
 * Carousels on the About Us page.
 *
 * One element drives both the champions rail and the series cards. It follows the same
 * pattern as js-trending: Swiper ships with the theme, so wait for the bundle rather than
 * assuming load order, and read slide sizing from CSS so the breakpoints stay in one place.
 */

class JsAboutCarousel extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('[data-carousel]');
    if (!this.root) return;

    this.prevEl = this.querySelector('[data-carousel-prev]');
    this.nextEl = this.querySelector('[data-carousel-next]');
    this.scrollbarEl = this.querySelector('[data-carousel-scrollbar]');

    // Some rails stack instead of scrolling at narrow widths; don't initialise there.
    const below = Number(this.dataset.stackBelow || 0);
    this.stackQuery = below ? window.matchMedia(`(max-width: ${below - 1}px)`) : null;

    this.ready().then(() => {
      this.sync();
      if (this.stackQuery) {
        this._onChange = () => this.sync();
        this.stackQuery.addEventListener('change', this._onChange);
      }
    });
  }

  /** Init or tear down to match the current breakpoint. */
  sync() {
    const stacked = this.stackQuery ? this.stackQuery.matches : false;
    if (stacked) {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      return;
    }
    this.init();
  }

  disconnectedCallback() {
    if (this.stackQuery && this._onChange) {
      this.stackQuery.removeEventListener('change', this._onChange);
    }
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  /** Swiper is loaded with `defer`, so it may not be on window yet. */
  ready() {
    if (window.Swiper) return Promise.resolve();

    return new Promise((resolve) => {
      const script = document.querySelector('script[src*="swiper-bundle"]');
      if (script) {
        script.addEventListener('load', () => resolve(), { once: true });
      }

      const timer = window.setInterval(() => {
        if (!window.Swiper) return;
        window.clearInterval(timer);
        resolve();
      }, 50);
    });
  }

  init() {
    if (!window.Swiper || !this.root || this.swiper) return;

    const gap = Number(this.dataset.gap || 16);

    this.swiper = new window.Swiper(this.root, {
      slidesPerView: 'auto',
      // Step a whole screen of cards, so the arrows never leave a card half cut.
      slidesPerGroupAuto: true,
      spaceBetween: gap,
      watchOverflow: true,
      a11y: {
        enabled: true,
        prevSlideMessage: this.dataset.prevLabel || 'Previous',
        nextSlideMessage: this.dataset.nextLabel || 'Next',
      },
      navigation:
        this.prevEl && this.nextEl
          ? { prevEl: this.prevEl, nextEl: this.nextEl }
          : false,
      scrollbar: this.scrollbarEl
        ? { el: this.scrollbarEl, draggable: true }
        : false,
      breakpoints: {
        1000: { spaceBetween: Number(this.dataset.gapDesktop || gap) },
      },
    });
  }
}

if (!customElements.get('js-about-carousel')) {
  customElements.define('js-about-carousel', JsAboutCarousel);
}
